/**
 * MCP Logger
 * Structured logging for MCP server
 */

import { MCPLogEntry } from '../types';

type LogLevel = 'debug' | 'info' | 'warning' | 'error';

export class MCPLogger {
  private level: LogLevel = 'info';
  private logs: MCPLogEntry[] = [];
  private maxLogs = 1000; // Keep last 1000 logs in memory

  /**
   * Set logging level
   */
  setLevel(level: LogLevel): void {
    this.level = level;
    console.log(`📝 MCP Logger level set to: ${level}`);
  }

  /**
   * Get current logging level
   */
  getLevel(): LogLevel {
    return this.level;
  }

  /**
   * Debug log
   */
  debug(message: string, context?: any): void {
    if (this.shouldLog('debug')) {
      this.log('debug', message, context);
    }
  }

  /**
   * Info log
   */
  info(message: string, context?: any): void {
    if (this.shouldLog('info')) {
      this.log('info', message, context);
    }
  }

  /**
   * Warning log
   */
  warning(message: string, context?: any): void {
    if (this.shouldLog('warning')) {
      this.log('warning', message, context);
    }
  }

  /**
   * Error log
   */
  error(message: string, context?: any): void {
    if (this.shouldLog('error')) {
      this.log('error', message, context);
    }
  }

  /**
   * Log a message
   */
  private log(level: LogLevel, message: string, context?: any): void {
    const entry: MCPLogEntry = {
      timestamp: new Date(),
      level,
      message,
      context,
      orgId: context?.orgId,
      userId: context?.userId,
      toolName: context?.toolName,
      duration: context?.duration
    };

    // Add to memory
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift(); // Remove oldest
    }

    // Console output with color
    const emoji = this.getEmoji(level);
    const color = this.getColor(level);
    const prefix = `${emoji} [MCP ${level.toUpperCase()}]`;
    
    if (context) {
      console.log(prefix, message, color, context);
    } else {
      console.log(prefix, message);
    }
  }

  /**
   * Check if should log at this level
   */
  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warning', 'error'];
    const currentLevelIndex = levels.indexOf(this.level);
    const messageLevelIndex = levels.indexOf(level);
    return messageLevelIndex >= currentLevelIndex;
  }

  /**
   * Get emoji for log level
   */
  private getEmoji(level: LogLevel): string {
    switch (level) {
      case 'debug': return '🔍';
      case 'info': return 'ℹ️';
      case 'warning': return '⚠️';
      case 'error': return '❌';
    }
  }

  /**
   * Get color for log level
   */
  private getColor(level: LogLevel): string {
    switch (level) {
      case 'debug': return 'color: gray';
      case 'info': return 'color: blue';
      case 'warning': return 'color: orange';
      case 'error': return 'color: red';
    }
  }

  /**
   * Get recent logs
   */
  getRecentLogs(limit?: number): MCPLogEntry[] {
    const logs = [...this.logs].reverse();
    return limit ? logs.slice(0, limit) : logs;
  }

  /**
   * Get logs by level
   */
  getLogsByLevel(level: LogLevel): MCPLogEntry[] {
    return this.logs.filter(log => log.level === level);
  }

  /**
   * Get logs by org
   */
  getLogsByOrg(orgId: string): MCPLogEntry[] {
    return this.logs.filter(log => log.orgId === orgId);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Export logs as JSON
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }
}

